import * as React from 'react';
import { BuyerInfo } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import { getBidderId, getBidderIsSuspended, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getBidderSubmittedBid } from '@/redux/modules/liveBid';
import { getIsProfileFullyCompleted } from '@/redux/modules/account/user/profileCompleted/profileCompleted.selectors';
import {
    getItemModelCatalogId,
    getItemModelIsPassed,
    getItemReserveMet,
    getItemSummaryBidderHasBid,
    getItemSummaryBidderHasHighBid,
    getItemSummaryBidderMaxBid,
    getItemSummaryBidderWonItem,
    getItemSummaryIsPlacingBid,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveBidderOutbid, getLiveItemDataLeadingBidder } from '@/redux/modules/console';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import {
    getRegistrationApproved,
    getRegistrationApprovedWithLimit,
    getRegistrationBidLimit,
    getRegistrationDeclined,
    getRegistrationPending,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getTotalBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { getUiHasLoggedInBefore } from '@/redux/modules/autoPopModal/autoPopModal.selectors';
import { useAppSelector } from '@/redux/hooks';

type Props = {
    itemId: number;
};

export const useGetBidCanvasBuyerData = ({ itemId }: Props): BuyerInfo => {
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderHasHighBid = useAppSelector((state) => getItemSummaryBidderHasHighBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const isPassed = useAppSelector((state) => getItemModelIsPassed(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemReserveMet(state, itemId));
    const bidderIsPlacingBid = useAppSelector((state) => getItemSummaryIsPlacingBid(state, itemId));
    const bidderMaxBid = useAppSelector((state) => getItemSummaryBidderMaxBid(state, itemId));

    const isLoggedIn = useAppSelector((state) => isUserLoggedIn(state));
    const isRecentlyLoggedIn = useAppSelector((state) => getUiHasLoggedInBefore(state));
    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const maxBidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));
    const declined = useAppSelector((state) => getRegistrationDeclined(state, catalogId));
    const limited = useAppSelector((state) => getRegistrationApprovedWithLimit(state, catalogId));
    const pending = useAppSelector((state) => getRegistrationPending(state, catalogId));
    const isProfileFullyCompleted = useAppSelector(getIsProfileFullyCompleted);

    const remainingBidLimitBalance = useAppSelector((state) => getTotalBidLimitBalance(state, catalogId));

    const submitted = useAppSelector(getBidderSubmittedBid);

    const bidderId = useAppSelector(getBidderId);
    const leadingBidder = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const isMyBid = bidderId === leadingBidder;

    const bidderOutbid = useAppSelector((state) => getLiveBidderOutbid(state, catalogId));
    const bidderHasWon = useAppSelector((state) => getItemSummaryBidderWonItem(state, itemId));
    const isLiveItem = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const bidderIsSuspended = useAppSelector(getBidderIsSuspended);
    const accountStatus = bidderIsSuspended ? 'suspended' : 'inGoodStanding';

    const registrationStatus = React.useMemo((): BuyerInfo['registrationStatus'] => {
        if (pending) {
            return 'pending';
        } else if (approved) {
            return 'approved';
        } else if (declined) {
            return 'declined';
        } else if (limited) {
            return 'limited';
        }

        return '';
    }, [pending, approved, declined, limited]);

    const bidStatus = React.useMemo((): BuyerInfo['bidStatus'] => {
        if (submitted) {
            return 'pending';
        } else if (bidderHasWon && !isPassed) {
            return 'won';
        } else if (
            (!isLiveItem && bidderHasBid && bidderHasHighBid && isReserveMet) ||
            (isLiveItem && isMyBid && bidderHasHighBid)
        ) {
            return 'leading';
        } else if (bidderHasBid && !isReserveMet) {
            return 'reserveNotMet';
        } else if (
            (isLiveItem && bidderOutbid) ||
            (!isLiveItem && bidderHasBid && !bidderHasHighBid && !bidderIsPlacingBid)
        ) {
            return 'outbid';
        }

        return '';
    }, [
        bidderHasBid,
        bidderHasHighBid,
        bidderHasWon,
        bidderIsPlacingBid,
        bidderOutbid,
        isLiveItem,
        isMyBid,
        isPassed,
        isReserveMet,
        submitted,
    ]);

    return React.useMemo(() => {
        return {
            accountStatus,
            bidderMaxBid: bidderMaxBid ?? 0,
            bidStatus,
            isLoggedIn,
            isProfileCompleted: isProfileFullyCompleted,
            isRecentlyLoggedIn,
            maxBidLimit,
            registrationStatus,
            remainingBidLimitBalance,
        };
    }, [
        accountStatus,
        bidderMaxBid,
        bidStatus,
        isLoggedIn,
        isProfileFullyCompleted,
        isRecentlyLoggedIn,
        maxBidLimit,
        registrationStatus,
        remainingBidLimitBalance,
    ]);
};
